import { useState, useEffect, useCallback } from "react";
import api, { BACKEND_URL } from "../lib/api";
import {
  GraduationCap, Calendar, ClipboardCheck, Award, Users, ChevronRight,
  Plus, RefreshCw, Clock, MapPin, User, CheckCircle2, AlertTriangle,
  BookOpen, Target, X, ChevronDown, Star, BarChart3, Eye, FileText,
  Pencil, Trash2, Save, Search, HelpCircle, Scissors, ThumbsUp, ThumbsDown
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CreateSessionModal, CreateEvaluationModal, CreateTrackModal, CreateResourceModal, QuickHaircutCheckModal } from "../components/TrainingForms";
import { useTour } from "../contexts/TourContext";

const SESSION_TYPE_LABELS = {
  classroom: "Classroom",
  demo: "Educator Demo",
  shadow: "Shadow Session",
  hands_on_model: "Model Session",
  quality_check: "Quality Check",
  certification: "Certification",
  remediation: "Remediation",
};

const SESSION_TYPE_COLORS = {
  classroom: { bg: "#EAF0F6", fg: "#274F7D" },
  demo: { bg: "#F3E8FF", fg: "#7C3AED" },
  shadow: { bg: "#FDF3E1", fg: "#805B10" },
  hands_on_model: { bg: "#E8F0E9", fg: "#2D5936" },
  quality_check: { bg: "#FFF0E6", fg: "#9A3412" },
  certification: { bg: "#F0F0F0", fg: "#1A1A1A" },
  remediation: { bg: "#FBEAE9", fg: "#8A2A23" },
};

const APPROVAL_STATUS = {
  not_ready: { label: "Not Ready", color: "#8A8A8A", bg: "#F3F3F3" },
  model_approved: { label: "Model Approved", color: "#805B10", bg: "#FDF3E1" },
  supervised_approved: { label: "Supervised", color: "#274F7D", bg: "#EAF0F6" },
  independent_approved: { label: "Independent", color: "#2D5936", bg: "#E8F0E9" },
  certified: { label: "Certified", color: "#1A1A1A", bg: "#F0F0F0" },
  needs_retraining: { label: "Needs Retraining", color: "#8A2A23", bg: "#FBEAE9" },
};

const SKILL_LEVELS = { junior: "#3B82F6", intermediate: "#F59E0B", advanced: "#8B5CF6" };

const tabs = [
  { id: "dashboard", label: "Dashboard", icon: BarChart3 },
  { id: "tracks", label: "Tracks", icon: BookOpen },
  { id: "sessions", label: "Sessions", icon: Calendar },
  { id: "evaluations", label: "Evaluations", icon: ClipboardCheck },
  { id: "approvals", label: "Approvals", icon: Award },
  { id: "profiles", label: "Profiles", icon: Users },
  { id: "library", label: "Library", icon: FileText },
  { id: "quality", label: "Quality", icon: Eye },
];

// ─── Dashboard Tab ───
function DashboardTab({ dashboard, quickChecks, onViewSession, onViewTrack, onDeleteQuickCheck, onViewQuickCheck }) {
  const d = dashboard || {};
  return (
    <div className="space-y-6" data-testid="training-dashboard-tab">
      {/* Top Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { icon: BookOpen, label: "Training Tracks", value: d.total_tracks || 0, color: "#B5A493" },
          { icon: Calendar, label: "Total Sessions", value: d.total_sessions || 0, color: "#8A8A8A" },
          { icon: ClipboardCheck, label: "Evaluations", value: d.total_evaluations || 0, color: "#274F7D" },
          { icon: Award, label: "Certified", value: d.certified_count || 0, color: "#2D5936" },
          { icon: Users, label: "In Pipeline", value: d.total_approvals || 0, color: "#805B10" },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5">
            <s.icon className="w-5 h-5 mb-2" style={{ color: s.color }} strokeWidth={1.5} />
            <p className="font-['Outfit'] text-3xl font-medium text-slate-900">{s.value}</p>
            <p className="text-xs text-slate-400 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Sessions */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="upcoming-sessions">
          <h3 className="font-['Outfit'] text-base font-medium text-slate-900 mb-4">Upcoming Sessions</h3>
          {(d.upcoming_sessions || []).length === 0 ? (
            <p className="text-sm text-slate-400 py-4">No upcoming sessions scheduled</p>
          ) : (
            <div className="space-y-3">
              {(d.upcoming_sessions || []).map(s => {
                const st = SESSION_TYPE_COLORS[s.session_type] || SESSION_TYPE_COLORS.classroom;
                return (
                  <div key={s.session_id} className="flex items-start gap-3 py-2 cursor-pointer hover:bg-[#FAFAFA] -mx-2 px-2 rounded-lg transition-colors"
                    onClick={() => onViewSession(s)} data-testid={`upcoming-${s.session_id}`}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: st.bg }}>
                      <Calendar className="w-4 h-4" style={{ color: st.fg }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{s.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-400">{s.date}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: st.bg, color: st.fg }}>
                          {SESSION_TYPE_LABELS[s.session_type] || s.session_type}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Needs Signoff */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="needs-signoff">
          <h3 className="font-['Outfit'] text-base font-medium text-slate-900 mb-4">Awaiting Signoff</h3>
          {(d.needs_signoff || []).length === 0 ? (
            <p className="text-sm text-slate-400 py-4">No pending signoffs</p>
          ) : (
            <div className="space-y-3">
              {(d.needs_signoff || []).map(a => {
                const as_ = APPROVAL_STATUS[a.status] || APPROVAL_STATUS.not_ready;
                return (
                  <div key={a.approval_id} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{a.trainee_name}</p>
                      <p className="text-xs text-slate-400">{a.service_name} — Score: {a.avg_score}</p>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: as_.bg, color: as_.color }}>
                      {as_.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Avg by Track */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="track-scores">
        <h3 className="font-['Outfit'] text-base font-medium text-slate-900 mb-4">Training Track Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(d.avg_by_track || []).map(t => (
            <div key={t.track_id} className="border border-slate-200 rounded-lg p-4 hover:border-rose-300 cursor-pointer transition-colors"
              onClick={() => onViewTrack(t.track_id)} data-testid={`track-stat-${t.track_id}`}>
              <p className="text-sm font-medium text-slate-900">{t.service_name}</p>
              <div className="flex items-center justify-between mt-2">
                <div>
                  <span className="font-['Outfit'] text-2xl font-medium text-slate-900">{t.avg_score || "—"}</span>
                  <span className="text-xs text-slate-400 ml-1">avg score</span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">{t.eval_count} evals</p>
                  <p className="text-xs text-slate-400">{t.trainee_count} trainees</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Evaluations */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="recent-evaluations">
        <h3 className="font-['Outfit'] text-base font-medium text-slate-900 mb-4">Recent Evaluations</h3>
        {(d.recent_evaluations || []).length === 0 ? (
          <p className="text-sm text-slate-400 py-4">No evaluations yet</p>
        ) : (
          <div className="space-y-2">
            {(d.recent_evaluations || []).slice(0, 6).map(e => (
              <div key={e.eval_id} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                <div>
                  <p className="text-sm font-medium text-slate-900">{e.trainee_name || "Trainee"}</p>
                  <p className="text-xs text-slate-400">{e.service_name} — by {e.educator_name || "Educator"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    e.overall_status === "pass" ? "bg-[#E8F0E9] text-emerald-600" :
                    e.overall_status === "fail" ? "bg-rose-50 text-rose-600" :
                    "bg-[#FDF3E1] text-amber-600"
                  }`}>
                    {e.overall_status === "pass" ? "Pass" : e.overall_status === "fail" ? "Fail" : "Needs Work"}
                  </span>
                  <span className="font-['Outfit'] text-lg font-semibold text-slate-900">{e.overall_score}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Quick Haircut Checks — light-touch floor walks */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="quick-checks-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Scissors className="w-4 h-4 text-rose-500" />
            <h3 className="font-['Outfit'] text-base font-medium text-slate-900">Recent Quick Haircut Checks</h3>
          </div>
          <span className="text-xs text-slate-400">{(quickChecks || []).length} total</span>
        </div>
        {(quickChecks || []).length === 0 ? (
          <p className="text-sm text-slate-400 py-4">No quick checks yet — tap the rose "Quick Haircut Check" button up top to record one in 30 seconds.</p>
        ) : (
          <div className="space-y-2">
            {(quickChecks || []).slice(0, 6).map(qc => {
              const total = (qc.thumbs_up_count || 0) + (qc.thumbs_down_count || 0);
              const pct = total > 0 ? Math.round((qc.thumbs_up_count / total) * 100) : 0;
              const tone = pct >= 80 ? "emerald" : pct >= 60 ? "amber" : "rose";
              const TONES = {
                emerald: "bg-[#E8F0E9] text-emerald-700",
                amber: "bg-[#FDF3E1] text-amber-700",
                rose: "bg-rose-50 text-rose-700",
              };
              return (
                <div key={qc.check_id}
                  onClick={() => onViewQuickCheck?.(qc)}
                  className="group flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-[#FAFAFA] transition-colors cursor-pointer"
                  data-testid={`quick-check-row-${qc.check_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900 truncate">{qc.stylist_name || "Stylist"}</span>
                      <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#F0F0F0] text-slate-700">
                        {qc.cut_type === "mens" ? "Men's" : "Women's"}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                      by {qc.submitted_by_name || "—"} · {qc.created_at ? new Date(qc.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                      {qc.notes ? ` · "${qc.notes.length > 40 ? qc.notes.slice(0, 40) + "…" : qc.notes}"` : ""}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${TONES[tone]}`}>
                    {qc.thumbs_up_count || 0}/{total} 👍
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                  {onDeleteQuickCheck && (
                    <button onClick={(e) => { e.stopPropagation(); onDeleteQuickCheck(qc.check_id); }}
                      data-testid={`delete-quick-check-${qc.check_id}`}
                      className="w-7 h-7 rounded-full opacity-0 group-hover:opacity-100 hover:bg-rose-50 flex items-center justify-center transition-all">
                      <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tracks Tab ───
function TracksTab({ tracks, onSelectTrack }) {
  return (
    <div data-testid="training-tracks-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {tracks.map(t => (
          <div key={t.track_id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-rose-300 transition-all cursor-pointer group"
            onClick={() => onSelectTrack(t)} data-testid={`track-card-${t.track_id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{t.service_name}</p>
                <p className="text-xs text-slate-400">{t.service_category}</p>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide"
                style={{ backgroundColor: `${SKILL_LEVELS[t.skill_level] || "#8A8A8A"}15`, color: SKILL_LEVELS[t.skill_level] || "#8A8A8A" }}>
                {t.skill_level}
              </span>
            </div>
            <p className="text-xs text-slate-600 line-clamp-2 mb-3">{t.description}</p>
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{t.time_standard_minutes} min</span>
              <span className="flex items-center gap-1"><Target className="w-3 h-3" />{(t.competencies || []).length} competencies</span>
            </div>
            {(t.prerequisites || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {t.prerequisites.map(p => (
                  <span key={p} className="text-[10px] px-1.5 py-0.5 bg-[#FAFAFA] rounded text-slate-600">{p}</span>
                ))}
              </div>
            )}
            <div className="mt-3 flex items-center justify-end text-xs text-rose-400 group-hover:text-slate-900 transition-colors">
              View track <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
            </div>
          </div>
        ))}
      </div>
      {tracks.length === 0 && (
        <div className="text-center py-16">
          <BookOpen className="w-10 h-10 text-[#E6E4E0] mx-auto mb-3" />
          <p className="text-sm text-slate-400">No training tracks yet</p>
        </div>
      )}
    </div>
  );
}

// ─── Sessions Tab ───
function SessionsTab({ sessions, evaluations, onSelectSession, onCompleteSession, onEvaluateFromSession }) {
  const [filter, setFilter] = useState("all");
  const today = new Date().toISOString().split("T")[0];

  // Pre-compute which sessions need attention so we can pin them up
  // top regardless of the active filter (sessions are the funnel into
  // evaluations — the manager should always see them first).
  const sessionMeta = sessions.map(s => {
    const trainees = s.trainee_ids || [];
    const evaledTraineeIds = new Set(
      evaluations
        .filter(e => e.session_id === s.session_id && e.track_id === s.track_id)
        .map(e => e.trainee_id)
    );
    const missing = trainees.filter(t => !evaledTraineeIds.has(t));
    const isPastDue = s.status === "scheduled" && s.date && s.date < today;
    const needsEvals = s.status === "completed" && missing.length > 0;
    return { ...s, _missing_count: missing.length, _is_past_due: isPastDue, _needs_evals: needsEvals };
  });

  const filtered = filter === "all" ? sessionMeta : sessionMeta.filter(s => s.status === filter);

  // Sort: past-due first, then needs-evals, then by date desc.
  filtered.sort((a, b) => {
    const priA = a._is_past_due ? 0 : a._needs_evals ? 1 : 2;
    const priB = b._is_past_due ? 0 : b._needs_evals ? 1 : 2;
    if (priA !== priB) return priA - priB;
    return (b.date || "").localeCompare(a.date || "");
  });

  const STATUS_PILLS = {
    scheduled: { label: "Scheduled", bg: "bg-[#EAF0F6]", fg: "text-blue-600" },
    completed: { label: "Completed", bg: "bg-[#E8F0E9]", fg: "text-emerald-600" },
    cancelled: { label: "Cancelled", bg: "bg-[#F3F3F3]", fg: "text-slate-400" },
  };

  return (
    <div data-testid="training-sessions-tab">
      <div className="flex items-center gap-2 mb-4">
        {["all", "scheduled", "completed", "cancelled"].map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`session-filter-${f}`}
            className={`text-xs px-3 py-1.5 rounded-full capitalize transition-colors ${
              filter === f ? "bg-slate-900 text-white" : "bg-[#F3F3F3] text-slate-600 hover:bg-[#E6E4E0]"
            }`}>{f}</button>
        ))}
      </div>
      <div className="space-y-3">
        {filtered.map(s => {
          const st = SESSION_TYPE_COLORS[s.session_type] || SESSION_TYPE_COLORS.classroom;
          const statusInfo = STATUS_PILLS[s.status] || STATUS_PILLS.scheduled;
          // Outline tone matches the action it needs.
          const outline = s._is_past_due ? "border-amber-300 ring-1 ring-amber-100"
                       : s._needs_evals ? "border-blue-200 ring-1 ring-blue-100"
                       : "border-slate-200";
          return (
            <div key={s.session_id} className={`bg-white border ${outline} rounded-2xl p-5 hover:shadow-sm transition-all cursor-pointer`}
              onClick={() => onSelectSession(s)} data-testid={`session-card-${s.session_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: st.bg }}>
                    <Calendar className="w-5 h-5" style={{ color: st.fg }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">{s.title}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: st.bg, color: st.fg }}>
                        {SESSION_TYPE_LABELS[s.session_type] || s.session_type}
                      </span>
                      <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{s.date} {s.start_time}–{s.end_time}</span>
                      {s.educator_name && <span className="text-xs text-slate-400 flex items-center gap-1"><User className="w-3 h-3" />{s.educator_name}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusInfo.bg} ${statusInfo.fg}`}>{statusInfo.label}</span>
                  {/* Workflow CTAs — sit on the card itself so the user
                      doesn't have to open a panel to drive the flow. */}
                  {s._is_past_due && onCompleteSession && (
                    <button onClick={e => { e.stopPropagation(); onCompleteSession(s); }}
                      data-testid={`session-complete-${s.session_id}`}
                      className="text-xs h-7 px-3 rounded-full bg-amber-500 text-white font-semibold hover:bg-amber-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Complete & evaluate
                    </button>
                  )}
                  {!s._is_past_due && s._needs_evals && onEvaluateFromSession && (
                    <button onClick={e => { e.stopPropagation(); onEvaluateFromSession(s); }}
                      data-testid={`session-evaluate-${s.session_id}`}
                      className="text-xs h-7 px-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 flex items-center gap-1">
                      <ClipboardCheck className="w-3 h-3" /> Evaluate {s._missing_count} trainee{s._missing_count === 1 ? "" : "s"}
                    </button>
                  )}
                </div>
              </div>
              {/* Status hint banner — explains *why* the card is highlighted. */}
              {(s._is_past_due || s._needs_evals) && (
                <div className={`mt-3 ml-14 text-[11px] px-3 py-1.5 rounded-lg ${s._is_past_due ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}`}>
                  {s._is_past_due
                    ? "Past date — mark this session complete to start evaluating trainees."
                    : `Session complete. ${s._missing_count} trainee${s._missing_count === 1 ? "" : "s"} still need${s._missing_count === 1 ? "s" : ""} an evaluation.`}
                </div>
              )}
              {s.agenda && <p className="text-xs text-slate-600 mt-2 ml-14 line-clamp-1">{s.agenda}</p>}
            </div>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-16">
          <Calendar className="w-10 h-10 text-[#E6E4E0] mx-auto mb-3" />
          <p className="text-sm text-slate-400">No sessions found</p>
        </div>
      )}
    </div>
  );
}

// ─── Evaluations Tab ───
function EvaluationsTab({ evaluations, onSelectEval, onDelete, onEditFull }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | pass | needs_work | fail
  const [trackFilter, setTrackFilter] = useState("all");   // service name | "all"
  const [traineeFilter, setTraineeFilter] = useState("all"); // trainee name | "all"
  const [sortBy, setSortBy] = useState("newest"); // newest | oldest | score_high | score_low | trainee

  // Build unique option lists from the data so filters are always in sync.
  const trackOptions = Array.from(new Set(evaluations.map(e => e.service_name).filter(Boolean))).sort();
  const traineeOptions = Array.from(new Set(evaluations.map(e => e.trainee_name).filter(Boolean))).sort();

  // Filter + sort pipeline.
  let filtered = evaluations.filter(e => {
    if (statusFilter !== "all" && e.overall_status !== statusFilter) return false;
    if (trackFilter !== "all" && e.service_name !== trackFilter) return false;
    if (traineeFilter !== "all" && e.trainee_name !== traineeFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${e.trainee_name || ""} ${e.service_name || ""} ${e.educator_name || ""} ${(e.strengths || []).join(" ")} ${(e.improvements || []).join(" ")}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  filtered = [...filtered].sort((a, b) => {
    if (sortBy === "newest") return (b.created_at || "").localeCompare(a.created_at || "");
    if (sortBy === "oldest") return (a.created_at || "").localeCompare(b.created_at || "");
    if (sortBy === "score_high") return (b.overall_score || 0) - (a.overall_score || 0);
    if (sortBy === "score_low") return (a.overall_score || 0) - (b.overall_score || 0);
    if (sortBy === "trainee") return (a.trainee_name || "").localeCompare(b.trainee_name || "");
    return 0;
  });

  const clearFilters = () => { setSearch(""); setStatusFilter("all"); setTrackFilter("all"); setTraineeFilter("all"); setSortBy("newest"); };
  const hasActiveFilters = search || statusFilter !== "all" || trackFilter !== "all" || traineeFilter !== "all" || sortBy !== "newest";

  // Status counts for the pills — based on the unfiltered set so counts
  // don't change as filters narrow.
  const counts = {
    all: evaluations.length,
    pass: evaluations.filter(e => e.overall_status === "pass").length,
    needs_work: evaluations.filter(e => e.overall_status === "needs_work").length,
    fail: evaluations.filter(e => e.overall_status === "fail").length,
  };

  const STATUS_PILLS = [
    { key: "all", label: "All", color: "bg-slate-900 text-white" },
    { key: "pass", label: "Pass", color: "bg-emerald-600 text-white" },
    { key: "needs_work", label: "Needs Work", color: "bg-amber-500 text-white" },
    { key: "fail", label: "Fail", color: "bg-rose-600 text-white" },
  ];

  return (
    <div data-testid="training-evaluations-tab">
      {/* Toolbar: search + filters + sort */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4 space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search trainee, service, educator, or notes…"
              data-testid="eval-search-input"
              className="w-full h-10 pl-10 pr-9 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300 bg-white" />
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75} />
            {search && (
              <button onClick={() => setSearch("")} data-testid="eval-search-clear"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center">
                <X className="w-3.5 h-3.5 text-slate-400" />
              </button>
            )}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              data-testid="eval-sort-select"
              className="h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300 bg-white text-slate-700">
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="score_high">Highest score</option>
              <option value="score_low">Lowest score</option>
              <option value="trainee">Trainee A→Z</option>
            </select>
          </div>
        </div>

        {/* Pill filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_PILLS.map(p => (
            <button key={p.key} onClick={() => setStatusFilter(p.key)} data-testid={`eval-status-${p.key}`}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors inline-flex items-center gap-1.5 ${
                statusFilter === p.key ? p.color : "bg-[#F3F3F3] text-slate-600 hover:bg-[#E6E4E0]"
              }`}>
              {p.label}
              <span className={`text-[10px] px-1.5 rounded-full ${statusFilter === p.key ? "bg-white/25" : "bg-white"}`}>
                {counts[p.key]}
              </span>
            </button>
          ))}

          <div className="h-5 w-px bg-slate-200 mx-1" />

          <select value={trackFilter} onChange={e => setTrackFilter(e.target.value)}
            data-testid="eval-track-filter"
            className="text-xs h-8 px-2.5 rounded-full border border-slate-200 bg-white text-slate-700 focus:outline-none focus:border-rose-300">
            <option value="all">All services</option>
            {trackOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <select value={traineeFilter} onChange={e => setTraineeFilter(e.target.value)}
            data-testid="eval-trainee-filter"
            className="text-xs h-8 px-2.5 rounded-full border border-slate-200 bg-white text-slate-700 focus:outline-none focus:border-rose-300">
            <option value="all">All trainees</option>
            {traineeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          {hasActiveFilters && (
            <button onClick={clearFilters} data-testid="eval-clear-filters"
              className="text-xs h-8 px-3 rounded-full text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1 font-medium">
              <X className="w-3 h-3" /> Clear
            </button>
          )}

          <span className="ml-auto text-xs text-slate-400" data-testid="eval-result-count">
            {filtered.length} of {evaluations.length}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map(e => (
          <div key={e.eval_id} className="group bg-white border border-slate-200 rounded-2xl p-5 hover:border-rose-300 hover:shadow-sm transition-all cursor-pointer relative"
            onClick={() => onSelectEval(e)} data-testid={`eval-card-${e.eval_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 truncate">{e.trainee_name || "Trainee"} — {e.service_name}</p>
                <p className="text-xs text-slate-400 mt-0.5">Evaluated by {e.educator_name || "Educator"} · {e.created_at?.split("T")[0]}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  e.overall_status === "pass" ? "bg-[#E8F0E9] text-emerald-600" :
                  e.overall_status === "fail" ? "bg-rose-50 text-rose-600" :
                  "bg-[#FDF3E1] text-amber-600"
                }`}>
                  {e.overall_status === "pass" ? "Pass" : e.overall_status === "fail" ? "Fail" : "Needs Work"}
                </span>
                <span className="font-['Outfit'] text-xl font-semibold text-slate-900 tabular-nums">{e.overall_score}</span>

                {/* Inline action buttons — appear on hover so the list
                    stays clean but edit/delete are one click away. */}
                {(onEditFull || onDelete) && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                    {onEditFull && (
                      <button onClick={ev => { ev.stopPropagation(); onEditFull(e); }}
                        data-testid={`eval-inline-edit-${e.eval_id}`}
                        title="Edit evaluation"
                        className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </button>
                    )}
                    {onDelete && (
                      <button onClick={ev => { ev.stopPropagation(); if (window.confirm(`Delete evaluation for ${e.trainee_name || "this trainee"}?`)) onDelete(e.eval_id, "evaluations"); }}
                        data-testid={`eval-inline-delete-${e.eval_id}`}
                        title="Delete evaluation"
                        className="w-8 h-8 rounded-full hover:bg-rose-50 flex items-center justify-center">
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            {(e.strengths || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {e.strengths.slice(0, 3).map((s, i) => (
                  <span key={`str-${s}-${i}`} className="text-[10px] px-1.5 py-0.5 bg-[#E8F0E9] text-emerald-600 rounded">{s}</span>
                ))}
                {(e.improvements || []).slice(0, 2).map((s, i) => (
                  <span key={`imp-${s}-${i}`} className="text-[10px] px-1.5 py-0.5 bg-[#FDF3E1] text-amber-600 rounded">{s}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {evaluations.length === 0 ? (
        <div className="text-center py-16">
          <ClipboardCheck className="w-10 h-10 text-[#E6E4E0] mx-auto mb-3" />
          <p className="text-sm text-slate-400">No evaluations yet</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16" data-testid="eval-no-results">
          <ClipboardCheck className="w-10 h-10 text-[#E6E4E0] mx-auto mb-3" />
          <p className="text-sm text-slate-500">No evaluations match these filters</p>
          <button onClick={clearFilters} className="text-xs text-rose-600 hover:underline mt-2 font-medium">Clear all filters</button>
        </div>
      ) : null}
    </div>
  );
}

// ─── Approvals Tab ───
function ApprovalsTab({ approvals, onUpdateApproval }) {
  const [filter, setFilter] = useState("all");
  const statuses = ["all", "not_ready", "model_approved", "supervised_approved", "independent_approved", "certified"];
  const filtered = filter === "all" ? approvals : approvals.filter(a => a.status === filter);

  return (
    <div data-testid="training-approvals-tab">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {statuses.map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`approval-filter-${s}`}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
              filter === s ? "bg-slate-900 text-white" : "bg-[#F3F3F3] text-slate-600 hover:bg-[#E6E4E0]"
            }`}>{s === "all" ? "All" : (APPROVAL_STATUS[s]?.label || s)}</button>
        ))}
      </div>
      <div className="space-y-3">
        {filtered.map(a => {
          const as_ = APPROVAL_STATUS[a.status] || APPROVAL_STATUS.not_ready;
          return (
            <div key={a.approval_id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`approval-card-${a.approval_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{a.trainee_name}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{a.service_name}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                    <span>Models: {a.model_sessions_completed || 0}</span>
                    <span>Avg: {a.avg_score || "—"}</span>
                    {a.educator_signoff && <span className="flex items-center gap-0.5 text-emerald-600"><CheckCircle2 className="w-3 h-3" />Educator</span>}
                    {a.manager_signoff && <span className="flex items-center gap-0.5 text-emerald-600"><CheckCircle2 className="w-3 h-3" />Manager</span>}
                  </div>
                  {a.notes && <p className="text-xs text-slate-400 mt-2 italic">{a.notes}</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: as_.bg, color: as_.color }}>
                    {as_.label}
                  </span>
                  {a.status !== "certified" && a.status !== "independent_approved" && (
                    <Select value="" onValueChange={(val) => onUpdateApproval(a.approval_id, val)}>
                      <SelectTrigger className="h-7 w-[140px] text-xs border-slate-200 rounded-lg" data-testid={`promote-${a.approval_id}`}>
                        <SelectValue placeholder="Promote..." />
                      </SelectTrigger>
                      <SelectContent>
                        {a.status === "not_ready" && <SelectItem value="model_approved">Model Approved</SelectItem>}
                        {(a.status === "not_ready" || a.status === "model_approved") && <SelectItem value="supervised_approved">Supervised</SelectItem>}
                        {(a.status === "model_approved" || a.status === "supervised_approved") && <SelectItem value="independent_approved">Independent</SelectItem>}
                        {a.status === "independent_approved" && <SelectItem value="certified">Certified</SelectItem>}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-16">
          <Award className="w-10 h-10 text-[#E6E4E0] mx-auto mb-3" />
          <p className="text-sm text-slate-400">No approvals in this category</p>
        </div>
      )}
    </div>
  );
}

// ─── Track Detail Panel ───
function TrackDetailPanel({ track, onClose, onDelete, onSave, onEditFull }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  if (!track) return null;
  const req = track.approval_requirements || {};

  const startEdit = () => { setForm({ service_name: track.service_name, description: track.description || "", quality_standards: track.quality_standards || "", consultation_standards: track.consultation_standards || "", finishing_standards: track.finishing_standards || "", educator_notes: track.educator_notes || "", time_standard_minutes: track.time_standard_minutes || 60 }); setEditing(true); };
  const saveEdit = async () => { await onSave(track.track_id, "tracks", form); setEditing(false); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/30 backdrop-blur-sm" data-testid="track-detail-panel">
      <div className="w-full max-w-[580px] h-full bg-white overflow-y-auto animate-slideInRight shadow-2xl">
        <div className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            {editing ? (
              <input value={form.service_name} onChange={e => setForm(f => ({ ...f, service_name: e.target.value }))} className="font-['Outfit'] text-xl font-medium text-slate-900 border-b border-rose-300 outline-none bg-transparent w-full" data-testid="edit-track-name" />
            ) : (
              <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">{track.service_name}</h2>
            )}
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-slate-400">{track.service_category}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase"
                style={{ backgroundColor: `${SKILL_LEVELS[track.skill_level] || "#8A8A8A"}15`, color: SKILL_LEVELS[track.skill_level] || "#8A8A8A" }}>
                {track.skill_level}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {editing ? (
              <>
                <button onClick={saveEdit} data-testid="save-track-edit" className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center hover:bg-[#1f4027]"><Save className="w-4 h-4" /></button>
                <button onClick={() => setEditing(false)} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center"><X className="w-4 h-4 text-slate-600" /></button>
              </>
            ) : (
              <>
                {onEditFull && (
                  <button onClick={() => onEditFull(track)} data-testid="edit-track-full-btn"
                    title="Edit every field of this training track"
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-[#E6E4E0] text-[11px] font-semibold uppercase tracking-wider text-slate-700 hover:bg-[#F3F3F3]">
                    <Pencil className="w-3 h-3" /> Edit all
                  </button>
                )}
                <button onClick={startEdit} data-testid="edit-track-btn" title="Quick edit basic fields" className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center"><Pencil className="w-4 h-4 text-slate-600" /></button>
                <button onClick={() => onDelete(track.track_id, "tracks")} data-testid="delete-track-btn" className="w-8 h-8 rounded-full hover:bg-rose-50 flex items-center justify-center"><Trash2 className="w-4 h-4 text-rose-600" /></button>
                <button data-testid="close-track-panel" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center"><X className="w-4 h-4 text-slate-600" /></button>
              </>
            )}
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div>
            {editing ? (
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3}
                className="w-full text-sm text-slate-600 border border-slate-200 rounded-lg p-2 outline-none focus:border-rose-300" data-testid="edit-track-description" />
            ) : (
              <p className="text-sm text-slate-600">{track.description}</p>
            )}
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
              {editing ? (
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /><input type="number" value={form.time_standard_minutes} onChange={e => setForm(f => ({ ...f, time_standard_minutes: parseInt(e.target.value) || 0 }))} className="w-12 border-b border-slate-200 text-center outline-none" /> min</span>
              ) : (
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{track.time_standard_minutes} min standard</span>
              )}
              <span className="flex items-center gap-1"><Target className="w-3 h-3" />{(track.competencies || []).length} competencies</span>
            </div>
          </div>

          {(track.prerequisites || []).length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Prerequisites</h4>
              <div className="flex flex-wrap gap-1.5">
                {track.prerequisites.map(p => <span key={p} className="text-xs px-2 py-1 bg-[#FAFAFA] rounded-lg text-slate-600">{p}</span>)}
              </div>
            </div>
          )}

          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Competencies</h4>
            <div className="space-y-2">
              {(track.competencies || []).map((c, i) => (
                <div key={c.id || c.name || `comp-${i}`} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-900">{c.name}</p>
                    <span className="text-xs font-semibold text-rose-400">{c.weight}%</span>
                  </div>
                  {c.description && <p className="text-xs text-slate-400 mt-1">{c.description}</p>}
                </div>
              ))}
            </div>
          </div>

          {(track.quality_standards || editing) && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Quality Standards</h4>
              {editing ? <textarea value={form.quality_standards} onChange={e => setForm(f => ({ ...f, quality_standards: e.target.value }))} rows={2} className="w-full text-sm border border-slate-200 rounded-lg p-2 outline-none focus:border-rose-300" /> : <p className="text-sm text-slate-600 bg-[#FAFAFA] rounded-lg p-3">{track.quality_standards}</p>}
            </div>
          )}
          {(track.consultation_standards || editing) && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Consultation Standards</h4>
              {editing ? <textarea value={form.consultation_standards} onChange={e => setForm(f => ({ ...f, consultation_standards: e.target.value }))} rows={2} className="w-full text-sm border border-slate-200 rounded-lg p-2 outline-none focus:border-rose-300" /> : <p className="text-sm text-slate-600 bg-[#FAFAFA] rounded-lg p-3">{track.consultation_standards}</p>}
            </div>
          )}
          {(track.finishing_standards || editing) && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Finishing Standards</h4>
              {editing ? <textarea value={form.finishing_standards} onChange={e => setForm(f => ({ ...f, finishing_standards: e.target.value }))} rows={2} className="w-full text-sm border border-slate-200 rounded-lg p-2 outline-none focus:border-rose-300" /> : <p className="text-sm text-slate-600 bg-[#FAFAFA] rounded-lg p-3">{track.finishing_standards}</p>}
            </div>
          )}
          {(track.educator_notes || editing) && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Educator Notes</h4>
              {editing ? <textarea value={form.educator_notes} onChange={e => setForm(f => ({ ...f, educator_notes: e.target.value }))} rows={2} className="w-full text-sm border border-slate-200 rounded-lg p-2 outline-none focus:border-rose-300" /> : <p className="text-sm text-slate-600 bg-[#FDF3E1] rounded-lg p-3 text-amber-600">{track.educator_notes}</p>}
            </div>
          )}

          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Approval Requirements</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-[#FAFAFA] rounded-lg p-3"><p className="text-xs text-slate-400">Min Model Sessions</p><p className="text-sm font-medium">{req.min_model_sessions || "—"}</p></div>
              <div className="bg-[#FAFAFA] rounded-lg p-3"><p className="text-xs text-slate-400">Min Score</p><p className="text-sm font-medium">{req.min_score || "—"}</p></div>
              <div className="bg-[#FAFAFA] rounded-lg p-3"><p className="text-xs text-slate-400">Educator Signoff</p><p className="text-sm font-medium">{req.educator_signoff ? "Required" : "Optional"}</p></div>
              <div className="bg-[#FAFAFA] rounded-lg p-3"><p className="text-xs text-slate-400">Manager Signoff</p><p className="text-sm font-medium">{req.manager_signoff ? "Required" : "Optional"}</p></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Session Detail Panel ───
function SessionDetailPanel({ session, onClose, onDelete, onSave, onEditFull }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  if (!session) return null;
  const st = SESSION_TYPE_COLORS[session.session_type] || SESSION_TYPE_COLORS.classroom;
  const model = session.model_info || {};

  const startEdit = () => { setForm({ title: session.title, date: session.date, start_time: session.start_time, end_time: session.end_time, agenda: session.agenda || "", status: session.status, notes: session.notes || "" }); setEditing(true); };
  const saveEdit = async () => { await onSave(session.session_id, "sessions", form); setEditing(false); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/30 backdrop-blur-sm" data-testid="session-detail-panel">
      <div className="w-full max-w-[580px] h-full bg-white overflow-y-auto animate-slideInRight shadow-2xl">
        <div className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            {editing ? (
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="font-['Outfit'] text-xl font-medium text-slate-900 border-b border-rose-300 outline-none bg-transparent w-full" data-testid="edit-session-title" />
            ) : (
              <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">{session.title}</h2>
            )}
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: st.bg, color: st.fg }}>
                {SESSION_TYPE_LABELS[session.session_type] || session.session_type}
              </span>
              {editing ? (
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="text-xs px-2 py-0.5 rounded-full border border-slate-200 outline-none" data-testid="edit-session-status">
                  <option value="scheduled">Scheduled</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
                </select>
              ) : (
                <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                  session.status === "completed" ? "bg-[#E8F0E9] text-emerald-600" :
                  session.status === "scheduled" ? "bg-[#EAF0F6] text-blue-600" : "bg-[#F3F3F3] text-slate-400"
                }`}>{session.status}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {editing ? (
              <>
                <button onClick={saveEdit} data-testid="save-session-edit" className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center hover:bg-[#1f4027]"><Save className="w-4 h-4" /></button>
                <button onClick={() => setEditing(false)} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center"><X className="w-4 h-4 text-slate-600" /></button>
              </>
            ) : (
              <>
                {onEditFull && (
                  <button onClick={() => onEditFull(session)} data-testid="edit-session-full-btn"
                    title="Edit every field of this session (trainees, educator, model, objectives...)"
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-[#E6E4E0] text-[11px] font-semibold uppercase tracking-wider text-slate-700 hover:bg-[#F3F3F3]">
                    <Pencil className="w-3 h-3" /> Edit all
                  </button>
                )}
                <button onClick={startEdit} data-testid="edit-session-btn" title="Quick edit" className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center"><Pencil className="w-4 h-4 text-slate-600" /></button>
                <button onClick={() => onDelete(session.session_id, "sessions")} data-testid="delete-session-btn" className="w-8 h-8 rounded-full hover:bg-rose-50 flex items-center justify-center"><Trash2 className="w-4 h-4 text-rose-600" /></button>
                <button data-testid="close-session-panel" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center"><X className="w-4 h-4 text-slate-600" /></button>
              </>
            )}
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            {editing ? (
              <>
                <div className="bg-[#FAFAFA] rounded-lg p-3"><p className="text-xs text-slate-400">Date</p><input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="text-sm font-medium bg-transparent outline-none w-full" data-testid="edit-session-date" /></div>
                <div className="bg-[#FAFAFA] rounded-lg p-3"><p className="text-xs text-slate-400">Time</p><div className="flex items-center gap-1"><input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} className="text-sm bg-transparent outline-none w-20" /><span>–</span><input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} className="text-sm bg-transparent outline-none w-20" /></div></div>
              </>
            ) : (
              <>
                <div className="bg-[#FAFAFA] rounded-lg p-3"><p className="text-xs text-slate-400">Date</p><p className="text-sm font-medium">{session.date}</p></div>
                <div className="bg-[#FAFAFA] rounded-lg p-3"><p className="text-xs text-slate-400">Time</p><p className="text-sm font-medium">{session.start_time} – {session.end_time}</p></div>
              </>
            )}
            <div className="bg-[#FAFAFA] rounded-lg p-3"><p className="text-xs text-slate-400">Educator</p><p className="text-sm font-medium">{session.educator_name || "—"}</p></div>
            <div className="bg-[#FAFAFA] rounded-lg p-3"><p className="text-xs text-slate-400">Service</p><p className="text-sm font-medium">{session.service_name || "—"}</p></div>
          </div>

          {(session.agenda || editing) && (
            <div><h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Agenda</h4>
              {editing ? <textarea value={form.agenda} onChange={e => setForm(f => ({ ...f, agenda: e.target.value }))} rows={2} className="w-full text-sm border border-slate-200 rounded-lg p-2 outline-none focus:border-rose-300" /> : <p className="text-sm text-slate-600">{session.agenda}</p>}
            </div>
          )}

          {(session.objectives || []).length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Objectives</h4>
              <ul className="space-y-1">
                {session.objectives.map((o, i) => (
                  <li key={`obj-${o}-${i}`} className="text-sm text-slate-600 flex items-start gap-2"><Target className="w-3.5 h-3.5 mt-0.5 text-rose-400 flex-shrink-0" />{o}</li>
                ))}
              </ul>
            </div>
          )}

          {session.prep_instructions && (
            <div><h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Prep Instructions</h4><p className="text-sm text-slate-600 bg-[#FDF3E1] rounded-lg p-3 text-amber-600">{session.prep_instructions}</p></div>
          )}

          {(session.materials_needed || []).length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Materials</h4>
              <div className="flex flex-wrap gap-1.5">
                {session.materials_needed.map((m, i) => <span key={`mat-${m}-${i}`} className="text-xs px-2 py-1 bg-[#FAFAFA] rounded-lg text-slate-600">{m}</span>)}
              </div>
            </div>
          )}

          {model.name && (
            <div className="border border-slate-200 rounded-lg p-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Model Information</h4>
              <div className="space-y-1">
                <p className="text-sm text-slate-900 font-medium">{model.name}</p>
                {model.hair_type && <p className="text-xs text-slate-400">Type: {model.hair_type}</p>}
                {model.starting_point && <p className="text-xs text-slate-400">Starting: {model.starting_point}</p>}
                {model.consent_signed && <p className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Consent signed</p>}
              </div>
            </div>
          )}

          {(session.notes || editing) && (
            <div><h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Notes</h4>
              {editing ? <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full text-sm border border-slate-200 rounded-lg p-2 outline-none focus:border-rose-300" /> : <p className="text-sm text-slate-600 bg-[#FAFAFA] rounded-lg p-3">{session.notes}</p>}
            </div>
          )}

          {Object.keys(session.attendance || {}).length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Attendance</h4>
              <div className="space-y-1">
                {Object.entries(session.attendance).map(([uid, status]) => (
                  <div key={uid} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{uid}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${status === "present" ? "bg-[#E8F0E9] text-emerald-600" : "bg-rose-50 text-rose-600"}`}>{status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Evaluation Detail Panel ───
function EvalDetailPanel({ evaluation, onClose, onDelete, onSave, onEditFull }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  if (!evaluation) return null;
  const e = evaluation;

  const startEdit = () => { setForm({ educator_notes: e.educator_notes || "", overall_status: e.overall_status || "pass", overall_score: e.overall_score || 0, formula_notes: e.formula_notes || "", timing_actual_minutes: e.timing_actual_minutes || 0 }); setEditing(true); };
  const saveEdit = async () => { await onSave(e.eval_id, "evaluations", form); setEditing(false); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/30 backdrop-blur-sm" data-testid="eval-detail-panel">
      <div className="w-full max-w-[580px] h-full bg-white overflow-y-auto animate-slideInRight shadow-2xl">
        <div className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">{e.trainee_name} — {e.service_name}</h2>
            <p className="text-xs text-slate-400 mt-0.5">Evaluated by {e.educator_name} · {e.created_at?.split("T")[0]}</p>
          </div>
          <div className="flex items-center gap-1">
            {editing ? (
              <>
                <button onClick={saveEdit} data-testid="save-eval-edit" className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center hover:bg-[#1f4027]"><Save className="w-4 h-4" /></button>
                <button onClick={() => setEditing(false)} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center"><X className="w-4 h-4 text-slate-600" /></button>
              </>
            ) : (
              <>
                {onEditFull && (
                  <button onClick={() => onEditFull(e)} data-testid="edit-eval-full-btn"
                    title="Edit every field of this evaluation (competency scores, photos, timing, notes)"
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-[#E6E4E0] text-[11px] font-semibold uppercase tracking-wider text-slate-700 hover:bg-[#F3F3F3]">
                    <Pencil className="w-3 h-3" /> Edit all
                  </button>
                )}
                <button onClick={startEdit} data-testid="edit-eval-btn" title="Quick edit" className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center"><Pencil className="w-4 h-4 text-slate-600" /></button>
                <button onClick={() => onDelete(e.eval_id, "evaluations")} data-testid="delete-eval-btn" className="w-8 h-8 rounded-full hover:bg-rose-50 flex items-center justify-center"><Trash2 className="w-4 h-4 text-rose-600" /></button>
                <button data-testid="close-eval-panel" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center"><X className="w-4 h-4 text-slate-600" /></button>
              </>
            )}
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-4">
            <div className="text-center">
              {editing ? (
                <div className="flex items-center gap-3">
                  <input type="number" value={form.overall_score} onChange={e2 => setForm(f => ({ ...f, overall_score: parseFloat(e2.target.value) || 0 }))}
                    className="font-['Outfit'] text-3xl font-medium text-slate-900 w-20 border-b border-rose-300 text-center outline-none bg-transparent" data-testid="edit-eval-score" />
                  <select value={form.overall_status} onChange={e2 => setForm(f => ({ ...f, overall_status: e2.target.value }))}
                    className="text-xs px-2 py-1 rounded-full border border-slate-200 outline-none" data-testid="edit-eval-status">
                    <option value="pass">Pass</option><option value="needs_work">Needs Work</option><option value="fail">Fail</option>
                  </select>
                </div>
              ) : (
                <>
                  <p className="font-['Outfit'] text-4xl font-medium text-slate-900">{e.overall_score}</p>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    e.overall_status === "pass" ? "bg-[#E8F0E9] text-emerald-600" :
                    e.overall_status === "fail" ? "bg-rose-50 text-rose-600" :
                    "bg-[#FDF3E1] text-amber-600"
                  }`}>
                    {e.overall_status === "pass" ? "Pass" : e.overall_status === "fail" ? "Fail" : "Needs Work"}
                  </span>
                </>
              )}
            </div>
            {(e.timing_actual_minutes > 0 || editing) && (
              <div className="ml-auto text-right">
                <p className="text-xs text-slate-400">Actual Time</p>
                {editing ? (
                  <input type="number" value={form.timing_actual_minutes} onChange={e2 => setForm(f => ({ ...f, timing_actual_minutes: parseInt(e2.target.value) || 0 }))}
                    className="font-['Outfit'] text-lg font-medium text-slate-900 w-16 border-b border-slate-200 text-right outline-none bg-transparent" />
                ) : (
                  <p className="font-['Outfit'] text-lg font-medium text-slate-900">{e.timing_actual_minutes} min</p>
                )}
              </div>
            )}
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Scorecard</h4>
            <div className="space-y-2">
              {(e.scores || []).map((s, i) => (
                <div key={s.competency || `score-${i}`} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-slate-900">{s.competency}</p>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        s.status === "pass" ? "bg-[#E8F0E9] text-emerald-600" :
                        s.status === "fail" ? "bg-rose-50 text-rose-600" :
                        "bg-[#FDF3E1] text-amber-600"
                      }`}>{s.status === "pass" ? "Pass" : s.status === "fail" ? "Fail" : "Needs Work"}</span>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(star => (
                          <Star key={star} className="w-3.5 h-3.5" fill={star <= s.score ? "#F59E0B" : "none"} stroke={star <= s.score ? "#F59E0B" : "#E6E4E0"} />
                        ))}
                      </div>
                    </div>
                  </div>
                  {s.notes && <p className="text-xs text-slate-400 mt-1">{s.notes}</p>}
                </div>
              ))}
            </div>
          </div>

          {(e.strengths || []).length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-600 mb-2">Strengths</h4>
              {e.strengths.map((s, i) => (
                <p key={`estr-${s}-${i}`} className="text-sm text-emerald-600 flex items-start gap-2 mb-1"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />{s}</p>
              ))}
            </div>
          )}
          {(e.improvements || []).length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-amber-600 mb-2">Areas for Improvement</h4>
              {e.improvements.map((s, i) => (
                <p key={`eimp-${s}-${i}`} className="text-sm text-amber-600 flex items-start gap-2 mb-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />{s}</p>
              ))}
            </div>
          )}
          {(e.formula_notes || editing) && (
            <div><h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Formula Notes</h4>
              {editing ? <input value={form.formula_notes} onChange={e2 => setForm(f => ({ ...f, formula_notes: e2.target.value }))} className="w-full text-xs font-mono border border-slate-200 rounded-lg p-2 outline-none focus:border-rose-300" /> : <p className="text-sm text-slate-600 bg-[#FAFAFA] rounded-lg p-3 font-mono text-xs">{e.formula_notes}</p>}
            </div>
          )}
          {(e.educator_notes || editing) && (
            <div><h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Educator Notes</h4>
              {editing ? <textarea value={form.educator_notes} onChange={e2 => setForm(f => ({ ...f, educator_notes: e2.target.value }))} rows={3} className="w-full text-sm border border-slate-200 rounded-lg p-2 outline-none focus:border-rose-300" /> : <p className="text-sm text-slate-600 bg-[#FAFAFA] rounded-lg p-3">{e.educator_notes}</p>}
            </div>
          )}
          {(e.photos || []).length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Before & After</h4>
              <div className="grid grid-cols-2 gap-3">
                {e.photos.map((p, i) => (
                  <div key={p.url || `photo-${i}`} className="relative rounded-lg overflow-hidden border border-slate-200">
                    <img src={`${BACKEND_URL}${p.url}`} alt={p.label} className="w-full h-40 object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
                      <span className="text-xs font-semibold text-white">{p.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Profiles Tab (Employee Training Progress) ───
function ProfilesTab({ onSelectProfile }) {
  const [staff, setStaff] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/staff");
        const providers = res.data.filter(s => ["stylist", "esthetician", "nail_tech", "colorist"].includes(s.role));
        setStaff(providers);
        const profileData = {};
        for (const s of providers) {
          try {
            const p = await api.get(`/training/profile/${s.user_id}`);
            profileData[s.user_id] = p.data;
          } catch (err) {
            // Per-staff profile fetch failure shouldn't block the whole list,
            // but we want visibility in the console.
            console.warn(`Failed to load training profile for ${s.user_id}:`, err);
          }
        }
        setProfiles(profileData);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="training-profiles-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {staff.map(s => {
          const p = profiles[s.user_id] || {};
          const approvalsList = p.approvals || [];
          const certified = approvalsList.filter(a => a.status === "independent_approved" || a.status === "certified").length;
          const inTraining = approvalsList.filter(a => a.status === "model_approved" || a.status === "supervised_approved" || a.status === "not_ready").length;
          return (
            <div key={s.user_id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-rose-300 transition-all cursor-pointer group"
              onClick={() => setSelectedUser(s.user_id === selectedUser ? null : s.user_id)} data-testid={`profile-card-${s.user_id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white" style={{ backgroundColor: s.color || "#B5A493" }}>
                  {s.name?.split(" ").map(n => n[0]).join("")}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{s.name}</p>
                  <p className="text-xs text-slate-400 capitalize">{s.title || s.role?.replace("_", " ")}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 mb-3">
                <div><p className="font-['Outfit'] text-xl font-medium text-emerald-600">{certified}</p><p className="text-[10px] text-slate-400">Approved</p></div>
                <div><p className="font-['Outfit'] text-xl font-medium text-amber-600">{inTraining}</p><p className="text-[10px] text-slate-400">In Training</p></div>
                <div><p className="font-['Outfit'] text-xl font-medium text-blue-600">{p.total_evaluations || 0}</p><p className="text-[10px] text-slate-400">Evals</p></div>
                <div><p className="font-['Outfit'] text-xl font-medium text-slate-900">{p.avg_score || "—"}</p><p className="text-[10px] text-slate-400">Avg Score</p></div>
              </div>
              {approvalsList.length > 0 && (
                <div className="space-y-1.5">
                  {approvalsList.map(a => {
                    const st = APPROVAL_STATUS[a.status] || APPROVAL_STATUS.not_ready;
                    return (
                      <div key={a.approval_id} className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">{a.service_name}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {approvalsList.length === 0 && <p className="text-xs text-slate-400 italic">No training tracks assigned</p>}

              {/* Expanded Profile Detail */}
              {selectedUser === s.user_id && (p.evaluations || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Recent Evaluations</h4>
                  {(p.evaluations || []).slice(0, 5).map(ev => (
                    <div key={ev.eval_id} className="flex items-center justify-between py-1">
                      <div><p className="text-xs text-slate-900">{ev.service_name}</p><p className="text-[10px] text-slate-400">{ev.created_at?.split("T")[0]}</p></div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${ev.overall_status === "pass" ? "bg-[#E8F0E9] text-emerald-600" : "bg-[#FDF3E1] text-amber-600"}`}>
                          {ev.overall_status === "pass" ? "Pass" : "Needs Work"}
                        </span>
                        <span className="text-xs font-semibold">{ev.overall_score}</span>
                      </div>
                    </div>
                  ))}
                  {(p.upcoming_sessions || []).length > 0 && (
                    <>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-3">Upcoming Sessions</h4>
                      {p.upcoming_sessions.map(sess => (
                        <div key={sess.session_id} className="text-xs text-slate-600 flex items-center gap-2">
                          <Calendar className="w-3 h-3 text-rose-400" />{sess.date} — {sess.title}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {staff.length === 0 && (
        <div className="text-center py-16"><Users className="w-10 h-10 text-[#E6E4E0] mx-auto mb-3" /><p className="text-sm text-slate-400">No service providers found</p></div>
      )}
    </div>
  );
}

// ─── Library Tab (Training Content) ───
function LibraryTab({ tracks, refreshKey, onDelete }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTrack, setFilterTrack] = useState("all");
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get("/training/resources").then(r => { setResources(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [refreshKey]);

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;

  const RESOURCE_ICONS = { video: "bg-[#F3E8FF] text-[#7C3AED]", guide: "bg-[#E8F0E9] text-emerald-600", sop: "bg-[#EAF0F6] text-blue-600", script: "bg-[#FDF3E1] text-amber-600", reference: "bg-[#FFF0E6] text-[#9A3412]", photo_reference: "bg-rose-50 text-rose-600" };
  const types = ["all", ...new Set(resources.map(r => r.type))];
  let filtered = resources;
  if (filterTrack !== "all") filtered = filtered.filter(r => (r.track_ids || []).includes(filterTrack));
  if (filterType !== "all") filtered = filtered.filter(r => r.type === filterType);

  return (
    <div data-testid="training-library-tab">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Track</span>
          <Select value={filterTrack} onValueChange={setFilterTrack}>
            <SelectTrigger className="h-8 w-[160px] text-xs border-slate-200 rounded-lg" data-testid="lib-track-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tracks</SelectItem>
              {tracks.map(t => <SelectItem key={t.track_id} value={t.track_id}>{t.service_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1.5">
          {types.map(t => (
            <button key={t} onClick={() => setFilterType(t)} data-testid={`lib-type-${t}`}
              className={`text-xs px-3 py-1.5 rounded-full capitalize transition-colors ${filterType === t ? "bg-slate-900 text-white" : "bg-[#F3F3F3] text-slate-600 hover:bg-[#E6E4E0]"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(r => {
          const iconClass = RESOURCE_ICONS[r.type] || RESOURCE_ICONS.guide;
          return (
            <div key={r.resource_id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-rose-300 transition-colors group relative" data-testid={`resource-${r.resource_id}`}>
              {onDelete && (
                <button onClick={() => onDelete(r.resource_id, "resources")}
                  data-testid={`delete-resource-${r.resource_id}`}
                  aria-label="Delete resource"
                  className="absolute top-3 right-3 w-7 h-7 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-50 flex items-center justify-center">
                  <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                </button>
              )}
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconClass}`}>
                  <FileText className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{r.title}</p>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F3F3F3] text-slate-600 uppercase font-bold tracking-wide">{r.type}</span>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-2 line-clamp-2">{r.description}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {(r.track_ids || []).map(tid => {
                  const tr = tracks.find(t => t.track_id === tid);
                  return tr ? <span key={tid} className="text-[9px] px-1.5 py-0.5 bg-[#FAFAFA] rounded text-slate-400">{tr.service_name}</span> : null;
                })}
              </div>
              {r.creator_name && <p className="text-[10px] text-rose-400 mt-2">by {r.creator_name}</p>}
            </div>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-16"><FileText className="w-10 h-10 text-[#E6E4E0] mx-auto mb-3" /><p className="text-sm text-slate-400">No resources found</p></div>
      )}
    </div>
  );
}

// ─── Quality & Readiness Tab ───
function QualityTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/training/quality-report").then(r => { setReport(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!report) return null;

  const SCORE_COLORS = { "90-100": "#1A1A1A", "80-89": "#8B5CF6", "70-79": "#2D5936", "60-69": "#805B10", "Below 60": "#8A2A23" };

  return (
    <div className="space-y-6" data-testid="training-quality-tab">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: "Avg Score", value: report.avg_overall_score, color: "#1A1A1A" },
          { label: "Evaluations", value: report.total_evaluations, color: "#274F7D" },
          { label: "Sessions", value: `${report.completed_sessions}/${report.total_sessions}`, color: "#2D5936" },
          { label: "Providers", value: report.total_providers, color: "#8A8A8A" },
          { label: "Tracks", value: report.total_tracks, color: "#B5A493" },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5">
            <p className="font-['Outfit'] text-3xl font-medium" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs text-slate-400 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Service Readiness Matrix */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="readiness-matrix">
          <h3 className="font-['Outfit'] text-base font-medium text-slate-900 mb-4">Service Readiness Matrix</h3>
          <div className="space-y-3">
            {report.readiness_matrix.map(r => (
              <div key={r.track_id} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-slate-900">{r.service_name}</p>
                  <span className="text-xs text-slate-400">{r.category}</span>
                </div>
                <div className="flex gap-2">
                  {r.certified > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F0F0F0] text-slate-900 font-medium">{r.certified} Certified</span>}
                  {r.independent > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E8F0E9] text-emerald-600 font-medium">{r.independent} Independent</span>}
                  {r.supervised > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#EAF0F6] text-blue-600 font-medium">{r.supervised} Supervised</span>}
                  {r.model_approved > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FDF3E1] text-amber-600 font-medium">{r.model_approved} Model</span>}
                  {r.not_ready > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F3F3F3] text-slate-400 font-medium">{r.not_ready} Not Ready</span>}
                  {r.total_trainees === 0 && <span className="text-[10px] text-slate-400 italic">No trainees assigned</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Score Distribution + Coaching Issues */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="score-distribution">
            <h3 className="font-['Outfit'] text-base font-medium text-slate-900 mb-4">Score Distribution</h3>
            <div className="space-y-2">
              {Object.entries(report.score_distribution).map(([range, count]) => (
                <div key={range} className="flex items-center gap-3">
                  <span className="text-xs text-slate-600 w-16">{range}</span>
                  <div className="flex-1 h-5 bg-[#F3F3F3] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{
                      width: `${Math.max((count / Math.max(report.total_evaluations, 1)) * 100, count > 0 ? 8 : 0)}%`,
                      backgroundColor: SCORE_COLORS[range] || "#8A8A8A",
                    }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-900 w-6 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {report.top_coaching_issues.length > 0 && (
            <div className="bg-[#FDF3E1] border border-[#F3D8A2] rounded-2xl p-6" data-testid="coaching-issues">
              <h3 className="font-['Outfit'] text-base font-medium text-amber-600 mb-3">Common Coaching Areas</h3>
              <div className="space-y-2">
                {report.top_coaching_issues.map((c, i) => (
                  <div key={`coach-${c.issue}-${i}`} className="flex items-center justify-between py-1">
                    <span className="text-sm text-amber-600">{c.issue}</span>
                    <span className="text-xs font-semibold text-amber-600 bg-white/50 px-2 py-0.5 rounded-full">{c.count}x</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Provider Readiness */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="provider-readiness">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-['Outfit'] text-base font-medium text-slate-900">Provider Readiness</h3>
          <p className="text-xs text-slate-400 mt-0.5">Service approval status for each team member</p>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-[#FAFAFA] border-b border-slate-200">
              <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Provider</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Approved</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">In Training</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Avg Score</th>
              <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Services</th>
            </tr>
          </thead>
          <tbody>
            {report.provider_readiness.map(p => (
              <tr key={p.user_id} className="border-b border-slate-200 hover:bg-[#FAFAFA]" data-testid={`readiness-row-${p.user_id}`}>
                <td className="px-6 py-3.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white" style={{ backgroundColor: p.color }}>
                      {p.name?.split(" ").map(n => n[0]).join("")}
                    </div>
                    <div><p className="text-sm font-medium text-slate-900">{p.name}</p><p className="text-[10px] text-slate-400 capitalize">{p.role?.replace("_", " ")}</p></div>
                  </div>
                </td>
                <td className="px-3 py-3.5 text-center">
                  <span className="font-['Outfit'] text-lg font-medium text-emerald-600">{p.certified}</span>
                </td>
                <td className="px-3 py-3.5 text-center">
                  <span className="font-['Outfit'] text-lg font-medium text-amber-600">{p.in_training}</span>
                </td>
                <td className="px-3 py-3.5 text-center">
                  <span className="font-['Outfit'] text-base font-medium text-slate-900">{p.avg_score || "—"}</span>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex flex-wrap gap-1">
                    {(p.approvals || []).map((a, i) => {
                      const st = APPROVAL_STATUS[a.status] || APPROVAL_STATUS.not_ready;
                      return <span key={a.approval_id || `${p.user_id}-${a.service_name}-${i}`} className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: st.bg, color: st.color }}>{a.service_name}</span>;
                    })}
                    {(p.approvals || []).length === 0 && <span className="text-[10px] text-slate-400 italic">None</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Quick Haircut Check — Detail Modal ───
// Renders the full evaluation that a row in the dashboard list points
// to. Read-only — you can only delete from here, not edit (these are
// floor-walk audits, intentionally immutable once saved).
function QuickHaircutCheckDetailModal({ check, onClose, onDelete }) {
  if (!check) return null;
  const total = (check.thumbs_up_count || 0) + (check.thumbs_down_count || 0);
  const pct = total > 0 ? Math.round((check.thumbs_up_count / total) * 100) : 0;
  const tone = pct >= 80 ? "emerald" : pct >= 60 ? "amber" : "rose";
  const TONES = {
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
    amber:   { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700",   dot: "bg-amber-500"   },
    rose:    { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-700",    dot: "bg-rose-500"    },
  };
  const t = TONES[tone];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="quick-haircut-check-detail-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center">
              <Scissors className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">
                {check.stylist_name || "Stylist"}
              </h2>
              <p className="text-[11px] text-slate-500">
                {check.cut_type === "mens" ? "Men's cut" : "Women's cut"} · Quick check
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="qhc-detail-close-btn"
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Score banner */}
        <div className={`mx-6 mt-5 ${t.bg} ${t.border} border rounded-xl p-4 flex items-center justify-between`}>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Result</p>
            <p className={`font-['Outfit'] text-2xl font-semibold ${t.text} mt-0.5`}>
              {check.thumbs_up_count || 0} <span className="text-slate-400 text-base font-normal">of</span> {total} 👍
            </p>
          </div>
          <div className="text-right">
            <p className={`font-['Outfit'] text-3xl font-bold ${t.text}`}>{pct}%</p>
            <p className="text-[11px] text-slate-500 mt-0.5">passing rate</p>
          </div>
        </div>

        {/* Submission metadata */}
        <div className="px-6 mt-5 grid grid-cols-2 gap-3 text-xs">
          <div className="bg-[#FAFAFA] rounded-lg p-3">
            <p className="uppercase tracking-wider text-[10px] font-bold text-slate-500">Submitted by</p>
            <p className="text-slate-900 font-medium mt-0.5">{check.submitted_by_name || "—"}</p>
          </div>
          <div className="bg-[#FAFAFA] rounded-lg p-3">
            <p className="uppercase tracking-wider text-[10px] font-bold text-slate-500">Date & time</p>
            <p className="text-slate-900 font-medium mt-0.5">
              {check.created_at ? new Date(check.created_at).toLocaleString(undefined, {
                month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit"
              }) : "—"}
            </p>
          </div>
        </div>

        {/* Question breakdown */}
        <div className="px-6 mt-5">
          <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Quality checks</p>
          <div className="space-y-2">
            {(check.answers || []).map((a, i) => {
              const up = !!a.thumbs_up;
              return (
                <div key={i} data-testid={`qhc-detail-answer-${i}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
                    up ? "bg-emerald-50/50 border-emerald-100" : "bg-rose-50/50 border-rose-100"
                  }`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    up ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"
                  }`}>
                    {up ? <ThumbsUp className="w-4 h-4" strokeWidth={2.5} /> : <ThumbsDown className="w-4 h-4" strokeWidth={2.5} />}
                  </div>
                  <p className="text-sm text-slate-800 flex-1">{a.question}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        {check.notes ? (
          <div className="px-6 mt-5">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Notes</p>
            <div className="bg-[#FFF8E7] border border-amber-100 rounded-xl p-3">
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{check.notes}</p>
            </div>
          </div>
        ) : null}

        {/* Footer */}
        <div className="px-6 py-5 mt-5 border-t border-slate-100 flex items-center justify-between">
          <Button variant="ghost" onClick={onDelete}
            data-testid="qhc-detail-delete-btn"
            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 rounded-full px-4 h-9">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
          </Button>
          <Button onClick={onClose} data-testid="qhc-detail-done-btn"
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-5 h-9">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─── Pipeline Action Panel ───
// "What needs my attention next" — a unified queue across the whole
// Sessions → Evaluations → Approvals flow. Always rendered above the
// active tab content so managers see action items the moment they land.
function PipelineActionsPanel({ actions, sessions, onComplete, onEvaluate, onPromote, onJumpToEvals }) {
  if (!actions || actions.total_actions === 0) return null;

  const sessById = Object.fromEntries((sessions || []).map(s => [s.session_id, s]));
  const pastScheduled = actions.past_scheduled_sessions || [];
  const missingEvals = actions.sessions_missing_evaluations || [];
  const signoffs = actions.evaluations_awaiting_signoff || [];
  const promotes = actions.approvals_ready_to_promote || [];

  // Group items into a single ordered list so the panel feels like a
  // queue, not a wall of cards.
  const items = [
    ...pastScheduled.map(s => ({
      key: `past-${s.session_id}`,
      kind: "complete_session",
      title: s.title || "Untitled session",
      sub: `${s.service_name || ""} · ${s.date}`,
      hint: `${s.trainee_ids?.length || 0} trainee${(s.trainee_ids?.length || 0) === 1 ? "" : "s"}`,
      cta: "Complete session",
      onClick: () => onComplete(sessById[s.session_id] || s),
      tone: "amber",
    })),
    ...missingEvals.flatMap(s =>
      (s.missing_trainees || []).map(t => ({
        key: `eval-${s.session_id}-${t.user_id}`,
        kind: "needs_eval",
        title: t.name || "Trainee",
        sub: `${s.service_name || ""} · ${s.title || ""} · ${s.date}`,
        hint: "Evaluate this trainee",
        cta: "Evaluate",
        onClick: () => onEvaluate(s, t),
        tone: "blue",
      }))
    ),
    ...signoffs.map(a => ({
      key: `sign-${a.approval_id}`,
      kind: "needs_signoff",
      title: a.trainee_name || "Trainee",
      sub: `${a.service_name || ""} · Avg score ${a.avg_score || "—"}`,
      hint: !a.educator_signoff ? "Awaiting educator signoff" : "Awaiting manager signoff",
      cta: "Open in Approvals",
      onClick: () => onJumpToEvals("approvals"),
      tone: "navy",
    })),
    ...promotes.map(a => ({
      key: `promote-${a.approval_id}`,
      kind: "ready_promote",
      title: a.trainee_name || "Trainee",
      sub: `${a.service_name || ""} · Avg ${a.avg_score} · ${a.model_sessions_completed || 0} sessions`,
      hint: "Ready to promote skill level",
      cta: "Promote",
      onClick: () => onPromote(a),
      tone: "emerald",
    })),
  ];

  const TONES = {
    amber:   { ring: "ring-amber-200",   chip: "bg-[#FDF3E1] text-amber-700",   dot: "bg-amber-500"   },
    blue:    { ring: "ring-blue-100",    chip: "bg-[#EAF0F6] text-blue-700",    dot: "bg-blue-500"    },
    navy:    { ring: "ring-slate-200",   chip: "bg-[#F0F0F0] text-slate-700",   dot: "bg-slate-700"   },
    emerald: { ring: "ring-emerald-100", chip: "bg-[#E8F0E9] text-emerald-700", dot: "bg-emerald-500" },
  };

  const KIND_LABELS = {
    complete_session: "Session complete?",
    needs_eval: "Needs evaluation",
    needs_signoff: "Awaiting signoff",
    ready_promote: "Ready to promote",
  };

  return (
    <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-[#FFF8E7] to-white p-5 mb-6"
      data-testid="training-pipeline-actions">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-amber-700" strokeWidth={2} />
          </div>
          <div>
            <h3 className="font-['Outfit'] text-base font-semibold text-slate-900">Action required</h3>
            <p className="text-xs text-slate-500">
              {actions.total_actions} item{actions.total_actions === 1 ? "" : "s"} waiting on you
            </p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-[11px] text-slate-500">
          {pastScheduled.length > 0 && <span className="px-2 py-1 rounded-full bg-[#FDF3E1] text-amber-700 font-medium">{pastScheduled.length} session{pastScheduled.length === 1 ? "" : "s"}</span>}
          {missingEvals.reduce((n, s) => n + (s.missing_trainees?.length || 0), 0) > 0 && (
            <span className="px-2 py-1 rounded-full bg-[#EAF0F6] text-blue-700 font-medium">
              {missingEvals.reduce((n, s) => n + (s.missing_trainees?.length || 0), 0)} eval{missingEvals.reduce((n, s) => n + (s.missing_trainees?.length || 0), 0) === 1 ? "" : "s"}
            </span>
          )}
          {signoffs.length > 0 && <span className="px-2 py-1 rounded-full bg-[#F0F0F0] text-slate-700 font-medium">{signoffs.length} signoff{signoffs.length === 1 ? "" : "s"}</span>}
          {promotes.length > 0 && <span className="px-2 py-1 rounded-full bg-[#E8F0E9] text-emerald-700 font-medium">{promotes.length} promotion{promotes.length === 1 ? "" : "s"}</span>}
        </div>
      </div>

      <div className="space-y-2">
        {items.slice(0, 6).map(it => {
          const tone = TONES[it.tone];
          return (
            <div key={it.key}
              data-testid={`pipeline-action-${it.key}`}
              className={`group flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-slate-200 hover:${tone.ring} hover:ring-2 transition-all`}>
              <span className={`w-2.5 h-2.5 rounded-full ${tone.dot} flex-shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900 truncate">{it.title}</span>
                  <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${tone.chip}`}>
                    {KIND_LABELS[it.kind]}
                  </span>
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5">{it.sub} · {it.hint}</p>
              </div>
              <button onClick={it.onClick}
                data-testid={`pipeline-action-cta-${it.key}`}
                className="text-xs font-semibold text-slate-900 hover:text-slate-700 px-3 h-8 rounded-full bg-[#FAFAFA] hover:bg-[#F0F0F0] border border-slate-200 flex items-center gap-1 flex-shrink-0">
                {it.cta} <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>

      {items.length > 6 && (
        <p className="text-[11px] text-slate-500 mt-3 text-center">
          + {items.length - 6} more in the queue
        </p>
      )}
    </div>
  );
}


// ─── Main Page ───
export default function TrainingStudioPage() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const { enabled: tourEnabled, start: startTour } = useTour();

  // Tour driver: when the Training tour fires a `tour:training:set-tab`
  // event, switch the active tab so its content (and any element the
  // next spotlight targets) is in the DOM before the spotlight resolves.
  useEffect(() => {
    const handler = (e) => {
      const tab = e?.detail?.tab;
      if (tab) setActiveTab(tab);
    };
    window.addEventListener("tour:training:set-tab", handler);
    return () => window.removeEventListener("tour:training:set-tab", handler);
  }, []);

  const [dashboard, setDashboard] = useState(null);
  const [actions, setActions] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedEval, setSelectedEval] = useState(null);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [showCreateEval, setShowCreateEval] = useState(false);
  // When set, opens the evaluation modal pre-filled with this seed
  // (session/track/trainee context). The modal closes by clearing it.
  const [evalPrefill, setEvalPrefill] = useState(null);
  const [showCreateTrack, setShowCreateTrack] = useState(false);
  const [showCreateResource, setShowCreateResource] = useState(false);
  // Quick Haircut Check — light-touch floor-walk evaluation.
  const [showQuickCheck, setShowQuickCheck] = useState(false);
  const [quickChecks, setQuickChecks] = useState([]);
  const [viewingCheck, setViewingCheck] = useState(null);
  // Full-edit modals — reuse the create forms in edit mode with a hydrated
  // `existing` prop. This gives the user access to EVERY field (not just the
  // inline subset) when modifying tracks / sessions / evaluations.
  const [editingTrack, setEditingTrack] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [editingEval, setEditingEval] = useState(null);
  const [libRefreshKey, setLibRefreshKey] = useState(0);

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, tracksRes, sessRes, evalRes, apprRes, actionsRes, qcRes] = await Promise.all([
        api.get("/training/dashboard"),
        api.get("/training/tracks"),
        api.get("/training/sessions"),
        api.get("/training/evaluations"),
        api.get("/training/approvals"),
        api.get("/training/pipeline-actions").catch(() => ({ data: { total_actions: 0 } })),
        api.get("/training/quick-haircut-checks").catch(() => ({ data: [] })),
      ]);
      setDashboard(dashRes.data);
      setTracks(tracksRes.data);
      setSessions(sessRes.data);
      setEvaluations(evalRes.data);
      setApprovals(apprRes.data);
      setActions(actionsRes.data);
      setQuickChecks(qcRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDeleteItem = async (id, type) => {
    const labelMap = { tracks: "training track", sessions: "session", evaluations: "evaluation", resources: "resource" };
    const label = labelMap[type] || type;
    if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/training/${type}/${id}`);
      toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)} deleted`);
      setSelectedTrack(null);
      setSelectedSession(null);
      setSelectedEval(null);
      fetchAll();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Delete failed", { type, id, status: err.response?.status, data: err.response?.data });
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleSaveItem = async (id, type, data) => {
    try {
      await api.put(`/training/${type}/${id}`, data);
      toast.success("Saved successfully");
      fetchAll();
      // Refresh the detail panel
      if (type === "tracks") {
        const res = await api.get(`/training/tracks/${id}`);
        setSelectedTrack(res.data);
      } else if (type === "sessions") {
        const res = await api.get(`/training/sessions/${id}`);
        setSelectedSession(res.data);
      } else if (type === "evaluations") {
        const res = await api.get(`/training/evaluations/${id}`);
        setSelectedEval(res.data);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Save failed", { type, id, status: err.response?.status, data: err.response?.data });
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleUpdateApproval = async (approvalId, newStatus) => {
    try {
      await api.put(`/training/approvals/${approvalId}`, { status: newStatus, educator_signoff: true });
      toast.success(`Status updated to ${APPROVAL_STATUS[newStatus]?.label || newStatus}`);
      fetchAll();
    } catch { toast.error("Failed to update"); }
  };

  // ─── Pipeline-action handlers ───
  // "Complete session" — mark it completed AND open the evaluation panel
  // pre-filled with the first trainee, so the educator doesn't have to
  // hunt for the next step. If there are multiple trainees, after the
  // first eval saves the actions panel will surface the next one.
  const handleCompleteSession = async (session) => {
    if (!session?.session_id) return;
    try {
      await api.post(`/training/sessions/${session.session_id}/complete`);
      toast.success("Session marked complete");
    } catch (err) {
      // Already completed → ignore; otherwise show error.
      if (err.response?.status !== 404) {
        console.warn("complete session failed", err);
      }
    }
    const firstTraineeId = (session.trainee_ids || [])[0];
    if (firstTraineeId) {
      setEvalPrefill({
        session_id: session.session_id,
        track_id: session.track_id,
        trainee_id: firstTraineeId,
        educator_id: session.educator_id || "",
        service_name: session.service_name || "",
      });
    } else {
      fetchAll();
    }
  };

  // "Evaluate trainee" — open the eval modal pre-filled with this
  // session + trainee.
  const handleEvaluateTrainee = (session, trainee) => {
    setEvalPrefill({
      session_id: session.session_id,
      track_id: session.track_id,
      trainee_id: trainee.user_id,
      educator_id: session.educator_id || "",
      service_name: session.service_name || "",
    });
  };

  // "Promote" — jump to Approvals tab and surface the row. Approval
  // promotion uses the existing select dropdown so the user can pick
  // the target tier.
  const handlePromoteApproval = (_approval) => {
    setActiveTab("approvals");
    toast.message("Pick the next tier from the promote dropdown");
  };

  const handleJumpTab = (tabId) => setActiveTab(tabId);

  const handleViewTrack = (trackId) => {
    const t = tracks.find(tr => tr.track_id === trackId);
    if (t) { setSelectedTrack(t); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="training-studio-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Training Studio</h1>
          <p className="text-slate-600 text-sm mt-1">Education, quality standards, and service certification</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Always-visible quick action: floor-walk haircut check */}
          <Button data-testid="quick-haircut-check-btn"
            onClick={() => setShowQuickCheck(true)}
            className="bg-rose-600 hover:bg-rose-700 text-white rounded-full h-10 px-5 text-sm gap-1.5"
            title="Light-touch on-the-floor haircut check (~30s)">
            <Scissors className="w-4 h-4" /> Quick Haircut Check
          </Button>
          {tourEnabled && (
            <button
              onClick={() => startTour("training")}
              data-testid="training-section-tour-btn"
              title="Take a guided tour of the Training module"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors"
            >
              <HelpCircle className="w-4 h-4 text-rose-500" />
              Section Tour
            </button>
          )}
          {activeTab === "tracks" && (
            <Button data-testid="create-track-btn" onClick={() => setShowCreateTrack(true)} className="bg-slate-900 text-white rounded-full h-10 px-5 text-sm hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-1.5" />New Track
            </Button>
          )}
          {activeTab === "sessions" && (
            <Button data-testid="create-session-btn" onClick={() => setShowCreateSession(true)} className="bg-slate-900 text-white rounded-full h-10 px-5 text-sm hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-1.5" />Schedule Session
            </Button>
          )}
          {activeTab === "evaluations" && (
            <Button data-testid="create-eval-btn" onClick={() => setShowCreateEval(true)} className="bg-slate-900 text-white rounded-full h-10 px-5 text-sm hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-1.5" />New Evaluation
            </Button>
          )}
          {activeTab === "library" && (
            <Button data-testid="create-resource-btn" onClick={() => setShowCreateResource(true)} className="bg-slate-900 text-white rounded-full h-10 px-5 text-sm hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-1.5" />Add Resource
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-6">
        {tabs.map(tab => (
          <button key={tab.id} data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-rose-500 text-slate-900"
                : "border-transparent text-slate-400 hover:text-slate-600"
            }`}>
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.id === "dashboard" && actions?.total_actions > 0 && (
              <span data-testid="tab-dashboard-badge"
                className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white">
                {actions.total_actions}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Pipeline Action Panel — surfaces what needs attention next, on
          every Training tab. Hidden when there's nothing to act on. */}
      <PipelineActionsPanel
        actions={actions}
        sessions={sessions}
        onComplete={handleCompleteSession}
        onEvaluate={handleEvaluateTrainee}
        onPromote={handlePromoteApproval}
        onJumpToEvals={handleJumpTab}
      />

      {/* Tab Content */}
      {activeTab === "dashboard" && (
        <DashboardTab
          dashboard={dashboard}
          quickChecks={quickChecks}
          onViewSession={setSelectedSession}
          onViewTrack={handleViewTrack}
          onViewQuickCheck={setViewingCheck}
          onDeleteQuickCheck={async (id) => {
            if (!window.confirm("Delete this quick check?")) return;
            try {
              await api.delete(`/training/quick-haircut-checks/${id}`);
              toast.success("Quick check deleted");
              fetchAll();
            } catch { toast.error("Failed to delete"); }
          }}
        />
      )}
      {activeTab === "tracks" && (
        <TracksTab tracks={tracks} onSelectTrack={setSelectedTrack} />
      )}
      {activeTab === "sessions" && (
        <SessionsTab
          sessions={sessions}
          evaluations={evaluations}
          onSelectSession={setSelectedSession}
          onCompleteSession={handleCompleteSession}
          onEvaluateFromSession={(session) => {
            const firstTrainee = (session.trainee_ids || [])[0];
            if (firstTrainee) {
              setEvalPrefill({
                session_id: session.session_id,
                track_id: session.track_id,
                trainee_id: firstTrainee,
                educator_id: session.educator_id || "",
                service_name: session.service_name || "",
              });
            }
          }}
        />
      )}
      {activeTab === "evaluations" && (
        <EvaluationsTab evaluations={evaluations} onSelectEval={setSelectedEval}
          onDelete={handleDeleteItem} onEditFull={setEditingEval} />
      )}
      {activeTab === "approvals" && (
        <ApprovalsTab approvals={approvals} onUpdateApproval={handleUpdateApproval} />
      )}
      {activeTab === "profiles" && (
        <ProfilesTab />
      )}
      {activeTab === "library" && (
        <LibraryTab tracks={tracks} refreshKey={libRefreshKey}
          onDelete={async (id, type) => {
            await handleDeleteItem(id, type);
            setLibRefreshKey(k => k + 1);
          }} />
      )}
      {activeTab === "quality" && (
        <QualityTab />
      )}

      {/* Detail Panels */}
      {selectedTrack && <TrackDetailPanel track={selectedTrack} onClose={() => setSelectedTrack(null)} onDelete={handleDeleteItem} onSave={handleSaveItem} onEditFull={setEditingTrack} />}
      {selectedSession && <SessionDetailPanel session={selectedSession} onClose={() => setSelectedSession(null)} onDelete={handleDeleteItem} onSave={handleSaveItem} onEditFull={setEditingSession} />}
      {selectedEval && <EvalDetailPanel evaluation={selectedEval} onClose={() => setSelectedEval(null)} onDelete={handleDeleteItem} onSave={handleSaveItem} onEditFull={setEditingEval} />}

      {/* Creation Modals */}
      {showCreateSession && <CreateSessionModal tracks={tracks} onClose={() => setShowCreateSession(false)} onCreated={() => { setShowCreateSession(false); fetchAll(); }} />}
      {showCreateEval && <CreateEvaluationModal sessions={sessions} tracks={tracks} onClose={() => setShowCreateEval(false)} onCreated={() => { setShowCreateEval(false); fetchAll(); }} />}
      {/* Pre-filled evaluation modal — opened from a session card or the
          pipeline-actions panel. Carries session/track/trainee context. */}
      {evalPrefill && <CreateEvaluationModal sessions={sessions} tracks={tracks} prefill={evalPrefill}
        onClose={() => setEvalPrefill(null)}
        onCreated={() => { setEvalPrefill(null); fetchAll(); }} />}
      {showCreateTrack && <CreateTrackModal onClose={() => setShowCreateTrack(false)} onCreated={() => { setShowCreateTrack(false); fetchAll(); }} />}
      {showCreateResource && <CreateResourceModal tracks={tracks} onClose={() => setShowCreateResource(false)} onCreated={() => { setShowCreateResource(false); setLibRefreshKey(k => k + 1); fetchAll(); }} />}
      {showQuickCheck && <QuickHaircutCheckModal onClose={() => setShowQuickCheck(false)} onCreated={() => { setShowQuickCheck(false); fetchAll(); }} />}
      {viewingCheck && <QuickHaircutCheckDetailModal check={viewingCheck} onClose={() => setViewingCheck(null)}
        onDelete={async () => {
          if (!window.confirm("Delete this quick check?")) return;
          try {
            await api.delete(`/training/quick-haircut-checks/${viewingCheck.check_id}`);
            toast.success("Quick check deleted");
            setViewingCheck(null);
            fetchAll();
          } catch { toast.error("Failed to delete"); }
        }} />}

      {/* Full-edit Modals — reuse the create forms, pre-hydrated with the
          existing record so every field is editable. */}
      {editingTrack && <CreateTrackModal existing={editingTrack} onClose={() => setEditingTrack(null)}
        onCreated={async () => {
          const id = editingTrack.track_id;
          setEditingTrack(null);
          await fetchAll();
          try { const res = await api.get(`/training/tracks/${id}`); setSelectedTrack(res.data); }
          catch (err) { console.warn("Failed to refresh track after edit", err); }
        }} />}
      {editingSession && <CreateSessionModal tracks={tracks} existing={editingSession} onClose={() => setEditingSession(null)}
        onCreated={async () => {
          const id = editingSession.session_id;
          setEditingSession(null);
          await fetchAll();
          try { const res = await api.get(`/training/sessions/${id}`); setSelectedSession(res.data); }
          catch (err) { console.warn("Failed to refresh session after edit", err); }
        }} />}
      {editingEval && <CreateEvaluationModal sessions={sessions} tracks={tracks} existing={editingEval} onClose={() => setEditingEval(null)}
        onCreated={async () => {
          const id = editingEval.eval_id;
          setEditingEval(null);
          await fetchAll();
          try { const res = await api.get(`/training/evaluations/${id}`); setSelectedEval(res.data); }
          catch (err) { console.warn("Failed to refresh evaluation after edit", err); }
        }} />}
    </div>
  );
}
